import type { components } from "@/api-schema";
import { AuditLogAction, AuditLogActorType } from "@/api-schema-enums";
import { formatDateToLocale } from "@/common/helpers/format-date";
import { cn } from "@/common/helpers/utils";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { ArrowRightIcon, BotIcon, CogIcon, UserIcon, type LucideIcon } from "lucide-react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

type AuditLog = components["schemas"]["AuditLog"];

const ACTION_STYLES = {
  [AuditLogAction.Create]: {
    label: "common:Created",
    colorVariant: BadgeColorVariant.Success,
    dotClassName: "bg-success",
  },
  [AuditLogAction.Update]: {
    label: "common:Updated",
    colorVariant: BadgeColorVariant.Primary,
    dotClassName: "bg-primary",
  },
  [AuditLogAction.Delete]: {
    label: "common:Deleted",
    colorVariant: BadgeColorVariant.Error,
    dotClassName: "bg-error",
  },
} as const satisfies Record<AuditLogAction, { label: string; colorVariant: BadgeColorVariant; dotClassName: string }>;

const ACTOR_TYPES = {
  [AuditLogActorType.User]: { label: "common:User", icon: UserIcon },
  [AuditLogActorType.Application]: { label: "common:AuditLogActorApplication", icon: BotIcon },
  [AuditLogActorType.System]: { label: "common:System", icon: CogIcon },
} as const satisfies Record<AuditLogActorType, { label: string; icon: LucideIcon }>;

function formatRawValue(value: unknown): string {
  if (value === null || value === undefined) return "null";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

export const AuditLogTimelineEntry = ({ entry }: { entry: AuditLog }) => {
  const { t } = useTranslation("common");
  const action = ACTION_STYLES[entry.action];
  const actor = ACTOR_TYPES[entry.actorType];
  const changes = Object.entries(entry.changes);

  return (
    <li className="group flex gap-4">
      <div className="flex flex-col items-center">
        <span className="bg-border h-1.75 w-0.5 group-first:invisible" />
        <span className={cn("ring-background relative z-1 size-2 shrink-0 rounded-full ring-4", action.dotClassName)} />
        <span className="bg-border w-0.5 grow group-last:hidden" />
      </div>
      <div className="min-w-0 flex-1 pb-7 group-last:pb-0">
        <div className="flex items-center justify-between gap-2">
          <Badge variant={BadgeVariant.Outlined} colorVariant={action.colorVariant}>
            {t(action.label)}
          </Badge>
          <time dateTime={entry.createdAt} className="text-muted-foreground text-xs">
            {formatDateToLocale(entry.createdAt)}
          </time>
        </div>
        <div className="text-muted-foreground mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          <span className="flex items-center gap-1.5 leading-none">
            <actor.icon className="size-3.5 shrink-0" />
            <span className="text-foreground font-medium">{t(actor.label)}</span>
            {entry.actorId && <span className="font-mono break-all">{entry.actorId}</span>}
          </span>
          {entry.operation && (
            <Badge variant={BadgeVariant.Outlined} colorVariant={BadgeColorVariant.Secondary} className="font-mono">
              {entry.operation}
            </Badge>
          )}
        </div>
        {changes.length > 0 && (
          <dl className="bg-muted/50 mt-3 grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 rounded-md border px-3 py-2 font-mono text-xs">
            {changes.map(([property, change]) => (
              <Fragment key={property}>
                <dt className="text-muted-foreground">{property}</dt>
                <dd className="flex flex-wrap items-center gap-1.5 break-all">
                  {entry.action === AuditLogAction.Update && (
                    <>
                      <span className="text-muted-foreground">{formatRawValue(change.old)}</span>
                      <ArrowRightIcon className="text-muted-foreground size-3 shrink-0" />
                    </>
                  )}
                  <span>{formatRawValue(change.new)}</span>
                </dd>
              </Fragment>
            ))}
          </dl>
        )}
      </div>
    </li>
  );
};
